import {
  AdminApiError,
  BULK_SURFACES,
  type AdminClient,
  type BulkBatchStatus,
  type BulkBatchView,
  type BulkEnvelopeViolation,
  type BulkMode,
  type BulkPreview,
  type BulkProposeInput,
  type BulkSurface,
  type IntegratorSummary,
} from '@apx/admin-sdk';
import { askConfirm, askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { showToast } from '../toast.js';
import { GAME_IDS, prettyGame } from '../games.js';

export interface BulkConfigPageDeps {
  client: AdminClient;
  integrators: IntegratorSummary[];
  userId: string;
  /** Global super_admin: the only principal who may approve or revert a batch. */
  isSuperAdmin: boolean;
  can: (perm: string) => boolean;
  /** Batch to open on load (`/bulk-config?batch=<id>`). */
  focusBatchId?: string;
}

const SURFACE_INFO: Record<BulkSurface, { label: string; hint: string; template?: unknown }> = {
  weights: { label: 'Weights', hint: 'Odds model — RNG bias, die faces, crit rates. Shape is per game.' },
  multipliers: { label: 'Multipliers', hint: 'Payout table per bet type. Shape is per game.' },
  timing: {
    label: 'Timing',
    hint: 'Phase durations and deadlines.',
    template: {
      bettingDurationMs: 15000,
      bettingClosingMs: 2000,
      playingMs: 3000,
      revealMs: 2000,
      showingResultMs: 3000,
      nextRoundDelayMs: 1000,
    },
  },
  scheduler: { label: 'Scheduler', hint: 'Auto-round opening cadence.', template: { paused: false } },
  abuse: {
    label: 'Abuse caps',
    hint: 'betCapPerRound, betRateLimitPerSecond. Each integrator’s kill switch is kept as-is and cannot be bulk-set.',
    template: { betCapPerRound: 50, betRateLimitPerSecond: 10 },
  },
  envelope: {
    label: 'Envelope',
    hint: 'Bounds the integrator’s own writes to tenant surfaces. Layered over the provider default envelope, per bound.',
    template: { table: { maxBet: { max: 100000 } } },
  },
  jackpot: { label: 'Jackpot', hint: 'Jackpot config only — pool balances are not touched. Shape is per game.' },
  maintenance: {
    label: 'Maintenance',
    hint: 'Per-game pause + player message.',
    template: { enabled: true, message: 'Game is temporarily under maintenance. Please check back shortly.' },
  },
};

/**
 * Bulk config: one provider-owned change across many integrators.
 *
 * Flow: compose → Preview (per-integrator diff, RTP cap, conflicts) → Submit
 * creates ONE pending batch → a different super admin approves it, which applies
 * every row in one transaction → any super admin can revert it.
 *
 * The value is edited as JSON. The per-game editors on each game page remain the
 * place for guided editing; "Load provider default" seeds this editor from the
 * provider baseline so a platform-wide push usually starts from a known-good
 * value rather than a blank.
 */
export async function renderBulkConfigPage(root: HTMLElement, deps: BulkConfigPageDeps): Promise<void> {
  const realIntegrators = deps.integrators.filter((i) => !i.isProvider);
  const providerIntegrator = deps.integrators.find((i) => i.isProvider) ?? null;

  root.innerHTML = `
    <h1 class="page-title">Bulk config</h1>
    <p class="page-sub">Apply one provider-owned setting to many integrators at once. A bulk change is previewed per integrator, approved once by a second super admin, and can be reverted as a unit.</p>

    <div class="card">
      <h2>New bulk change</h2>
      <div class="field">
        <span class="field-label">Change</span>
        <div class="bulk-checks">
          <label><input type="radio" name="bk-mode" value="set" checked /> Set a value on each integrator</label>
          <label><input type="radio" name="bk-mode" value="inherit" /> Reset to provider default — remove each integrator’s own value so it follows the provider default, including future provider edits</label>
        </div>
      </div>
      <div class="bulk-grid">
        <div class="field" style="margin:0">
          <label class="field-label" for="bk-surface">Surface</label>
          <select id="bk-surface" class="field-input">
            ${BULK_SURFACES.map((s) => `<option value="${s}">${SURFACE_INFO[s].label}</option>`).join('')}
          </select>
          <p id="bk-surface-hint" class="field-hint"></p>
        </div>
        <div class="field" style="margin:0">
          <span class="field-label">Games</span>
          <div class="bulk-checks" id="bk-games">
            ${GAME_IDS.map(
              (g) => `<label><input type="checkbox" value="${g}" /> ${escapeHtml(prettyGame(g))}</label>`,
            ).join('')}
          </div>
          <p class="field-hint">Multi-game works for surfaces every game shares (timing, abuse, maintenance, scheduler, envelope).</p>
        </div>
        <div class="field" style="margin:0">
          <span class="field-label">Integrators</span>
          <div class="bulk-checks">
            <label><input type="radio" name="bk-scope" value="all" checked /> Every integrator that has the game</label>
            <label><input type="radio" name="bk-scope" value="some" /> Only selected</label>
          </div>
          <div id="bk-integrators" class="bulk-checks bulk-integrators hidden">
            ${realIntegrators
              .map(
                (i) =>
                  `<label><input type="checkbox" value="${i.id}" /> ${escapeHtml(i.name)} <code>${escapeHtml(i.slug)}</code>${i.status !== 'active' ? ` <span class="pill warn">${escapeHtml(i.status)}</span>` : ''}</label>`,
              )
              .join('')}
          </div>
        </div>
      </div>
      <div class="field" id="bk-value-field">
        <div class="row" style="justify-content:space-between;align-items:center">
          <label class="field-label" for="bk-value">Value (JSON)</label>
          <span>
            <button id="bk-template" class="ghost" type="button">Use template</button>
            <button id="bk-provider" class="ghost" type="button" ${providerIntegrator ? '' : 'disabled title="No provider default integrator"'}>Load provider default</button>
          </span>
        </div>
        <textarea id="bk-value" class="field-input mono-input bulk-value" rows="10" spellcheck="false">{}</textarea>
      </div>
      <div class="field">
        <label class="field-label" for="bk-note">Note for the approver</label>
        <input id="bk-note" class="field-input" type="text" maxlength="500" placeholder="Why this change, ticket link…" />
      </div>
      <div class="row" style="gap:8px;align-items:center">
        <button id="bk-preview" class="primary" type="button">Preview</button>
        <button id="bk-submit" type="button" disabled>Submit for approval</button>
        <span id="bk-msg" class="muted"></span>
      </div>
      <div id="bk-preview-out" style="margin-top:14px"></div>
    </div>

    <div class="card">
      <div class="row" style="justify-content:space-between;align-items:center">
        <h2 style="margin:0">Batches</h2>
        <div class="seg" id="bk-filter">
          ${(['pending', 'approved', 'reverted', 'rejected', 'all'] as const)
            .map((s) => `<button class="seg-btn${s === 'pending' ? ' active' : ''}" data-status="${s}">${s}</button>`)
            .join('')}
        </div>
      </div>
      <div id="bk-batches" style="margin-top:12px"><p class="muted">Loading…</p></div>
      <div id="bk-batches-more" class="hidden" style="margin-top:12px"><button id="bk-more">Load more</button></div>
    </div>
  `;

  const $ = <T extends HTMLElement>(sel: string): T => root.querySelector<T>(sel)!;
  const surfaceEl = $<HTMLSelectElement>('#bk-surface');
  const valueEl = $<HTMLTextAreaElement>('#bk-value');
  const submitBtn = $<HTMLButtonElement>('#bk-submit');
  const msg = $('#bk-msg');
  const integratorName = (id: string | null) => {
    const i = id ? deps.integrators.find((x) => x.id === id) : undefined;
    return i ? i.slug : (id?.slice(0, 8) ?? '—');
  };

  // ── Compose ────────────────────────────────────────────────────────────────
  // The preview that enabled Submit, keyed by the exact inputs it was run with.
  // Any edit invalidates it, so what gets submitted is what was previewed.
  let previewedKey: string | null = null;

  function paintSurfaceHint(): void {
    const info = SURFACE_INFO[surfaceEl.value as BulkSurface];
    $('#bk-surface-hint').textContent = info.hint;
    $<HTMLButtonElement>('#bk-template').disabled = info.template === undefined;
  }

  function invalidatePreview(): void {
    previewedKey = null;
    submitBtn.disabled = true;
  }

  const currentMode = (): BulkMode =>
    (root.querySelector<HTMLInputElement>('input[name="bk-mode"]:checked')?.value as BulkMode) ?? 'set';

  function readInput(): BulkProposeInput | string {
    const gameIds = Array.from(root.querySelectorAll<HTMLInputElement>('#bk-games input:checked')).map((c) => c.value);
    if (gameIds.length === 0) return 'Pick at least one game.';
    const mode = currentMode();
    let value: unknown;
    if (mode === 'set') {
      try {
        value = JSON.parse(valueEl.value);
      } catch (err) {
        return `Value is not valid JSON: ${err instanceof Error ? err.message : String(err)}`;
      }
    }
    const some = root.querySelector<HTMLInputElement>('input[name="bk-scope"]:checked')?.value === 'some';
    const integratorIds = some
      ? Array.from(root.querySelectorAll<HTMLInputElement>('#bk-integrators input:checked')).map((c) => c.value)
      : undefined;
    if (some && integratorIds!.length === 0) return 'Pick at least one integrator, or choose every integrator.';
    const note = $<HTMLInputElement>('#bk-note').value.trim();
    return {
      surface: surfaceEl.value as BulkSurface,
      gameIds,
      ...(integratorIds ? { integratorIds } : {}),
      mode,
      ...(mode === 'set' ? { value } : {}),
      ...(note ? { note } : {}),
    };
  }

  root.querySelectorAll('input[name="bk-mode"]').forEach((el) =>
    el.addEventListener('change', () => {
      // An inherit carries no value — the provider default IS the value.
      $('#bk-value-field').classList.toggle('hidden', currentMode() === 'inherit');
      invalidatePreview();
      $('#bk-preview-out').innerHTML = '';
    }),
  );

  const keyOf = (input: BulkProposeInput) =>
    JSON.stringify({ ...input, note: undefined });

  surfaceEl.addEventListener('change', () => {
    paintSurfaceHint();
    invalidatePreview();
    $('#bk-preview-out').innerHTML = '';
  });
  root.querySelectorAll('#bk-games input, input[name="bk-scope"], #bk-integrators input').forEach((el) =>
    el.addEventListener('change', () => {
      $('#bk-integrators').classList.toggle(
        'hidden',
        root.querySelector<HTMLInputElement>('input[name="bk-scope"]:checked')?.value !== 'some',
      );
      invalidatePreview();
    }),
  );
  valueEl.addEventListener('input', invalidatePreview);

  $('#bk-template').addEventListener('click', () => {
    const t = SURFACE_INFO[surfaceEl.value as BulkSurface].template;
    if (t === undefined) return;
    valueEl.value = JSON.stringify(t, null, 2);
    invalidatePreview();
  });

  $('#bk-provider').addEventListener('click', async () => {
    if (!providerIntegrator) return;
    const game = root.querySelector<HTMLInputElement>('#bk-games input:checked')?.value;
    if (!game) {
      showToast('Pick a game first — provider defaults are per game.', 'warn');
      return;
    }
    try {
      const { value } = await deps.client.getSurface(providerIntegrator.id, game, surfaceEl.value as BulkSurface);
      if (value === null || value === undefined) {
        showToast(`The provider default sets no ${surfaceEl.value} for ${prettyGame(game)}.`, 'warn');
        return;
      }
      valueEl.value = JSON.stringify(value, null, 2);
      invalidatePreview();
    } catch (err) {
      showToast(describe(err), 'error');
    }
  });

  $('#bk-preview').addEventListener('click', async () => {
    const input = readInput();
    if (typeof input === 'string') {
      setMsg(input, 'error');
      return;
    }
    setMsg('Previewing…', 'muted');
    try {
      const res = await deps.client.previewBulkConfig(input);
      $('#bk-preview-out').innerHTML = renderPreview(res);
      const ok = res.summary.changed > 0 && res.summary.blocked === 0;
      previewedKey = ok ? keyOf(input) : null;
      submitBtn.disabled = !ok;
      setMsg(
        res.summary.blocked > 0
          ? input.mode === 'inherit'
            ? `${res.summary.blocked} row(s) would inherit a provider default over 100% RTP.`
            : `${res.summary.blocked} row(s) would put bets over 100% RTP — adjust the value.`
          : res.summary.changed === 0
            ? input.mode === 'inherit'
              ? 'Every selected integrator already follows the provider default.'
              : 'Nothing would change.'
            : `Ready: ${res.summary.changed} change(s) across ${res.summary.integrators} integrator(s).`,
        ok ? 'muted' : 'error',
      );
    } catch (err) {
      $('#bk-preview-out').innerHTML = '';
      invalidatePreview();
      setMsg(describeWithDetails(err), 'error');
    }
  });

  submitBtn.addEventListener('click', async () => {
    const input = readInput();
    if (typeof input === 'string' || keyOf(input) !== previewedKey) {
      invalidatePreview();
      setMsg('Inputs changed since the preview — preview again.', 'error');
      return;
    }
    submitBtn.disabled = true;
    try {
      const res = await deps.client.proposeBulkConfig(input);
      showToast(`Batch ${res.batch.id.slice(0, 8)} submitted — a different super admin must approve it.`, 'success');
      invalidatePreview();
      $('#bk-preview-out').innerHTML = '';
      setMsg('', 'muted');
      batchFilter = 'pending';
      paintFilter();
      await loadBatches(true);
    } catch (err) {
      submitBtn.disabled = false;
      setMsg(describeWithDetails(err), 'error');
    }
  });

  function setMsg(text: string, cls: 'muted' | 'error'): void {
    msg.textContent = text;
    msg.className = cls;
  }

  function renderPreview(res: BulkPreview): string {
    const s = res.summary;
    const inherit = res.preview.some((p) => 'inheritedConfig' in p);
    const skipped = res.skipped.length
      ? `<p class="warn-text">Skipped (game not provisioned, never auto-added): ${res.skipped
          .map((k) => `${escapeHtml(integratorName(k.integratorId))} · ${escapeHtml(prettyGame(k.gameId))}`)
          .join(', ')}</p>`
      : '';
    return `
      <div class="row" style="gap:8px;flex-wrap:wrap;margin-bottom:8px">
        <span class="pill">${s.integrators} integrator(s)</span>
        <span class="pill live">${s.changed} change(s)</span>
        <span class="pill">${s.unchanged} unchanged</span>
        ${s.blocked ? `<span class="pill warn">${s.blocked} over RTP cap</span>` : ''}
        ${s.outOfEnvelope ? `<span class="pill warn">${s.outOfEnvelope} already outside new envelope</span>` : ''}
      </div>
      ${supersedeSummary(s.supersedes)}
      ${envelopeSummary(s.outOfEnvelope)}
      ${skipped}
      <div class="data-table-wrap"><table class="data-table">
        <thead><tr><th>Integrator</th><th>Game</th><th>Result</th><th>Current</th><th>${inherit ? 'Will follow provider default' : 'Proposed'}</th></tr></thead>
        <tbody>${res.preview
          .map((p) => {
            const result = p.rtpCapViolations.length
              ? `<span class="pill warn">over cap</span><div class="muted">${escapeHtml(p.rtpCapViolations.join(', '))}</div>`
              : p.changed
                ? `<span class="pill live">${inherit ? 'override removed' : 'changes'}</span>`
                : `<span class="pill">${inherit ? 'already inherits' : 'unchanged'}</span>`;
            // An inherit shows what the integrator will run on; a leftover
            // preserved key (kill switch) is still listed under it.
            const proposed = inherit
              ? `${jsonBlock(p.inheritedConfig, 'game defaults')}${
                  p.nextConfig !== null ? `<div class="muted">keeps ${jsonBlock(p.nextConfig)}</div>` : ''
                }`
              : jsonBlock(p.nextConfig);
            const conflict = p.changed ? supersedeWarning(p.conflictingApprovalIds) : '';
            const outOfEnvelope = envelopeWarning(p.envelopeViolations);
            const status = p.integratorStatus !== 'active' ? ` <span class="pill warn">${escapeHtml(p.integratorStatus)}</span>` : '';
            return `<tr>
              <td>${escapeHtml(p.integratorName)} <code>${escapeHtml(p.integratorSlug)}</code>${status}</td>
              <td>${escapeHtml(prettyGame(p.gameId))}</td>
              <td>${result}${conflict}${outOfEnvelope}</td>
              <td>${jsonBlock(p.prevConfig, 'no override')}</td>
              <td>${proposed}</td>
            </tr>`;
          })
          .join('')}</tbody>
      </table></div>`;
  }

  // ── Batches ────────────────────────────────────────────────────────────────
  let batchFilter: BulkBatchStatus | 'all' = deps.focusBatchId ? 'all' : 'pending';
  let batches: BulkBatchView[] = [];
  let nextCursor: string | null = null;
  const openBatches = new Set<string>(deps.focusBatchId ? [deps.focusBatchId] : []);

  function paintFilter(): void {
    root.querySelectorAll<HTMLButtonElement>('#bk-filter .seg-btn').forEach((b) =>
      b.classList.toggle('active', b.dataset.status === batchFilter),
    );
  }
  paintFilter();
  root.querySelectorAll<HTMLButtonElement>('#bk-filter .seg-btn').forEach((b) =>
    b.addEventListener('click', () => {
      batchFilter = b.dataset.status as BulkBatchStatus | 'all';
      paintFilter();
      void loadBatches(true);
    }),
  );
  $('#bk-more').addEventListener('click', () => void loadBatches(false));

  async function loadBatches(reset: boolean): Promise<void> {
    try {
      const res = await deps.client.listBulkBatches({
        status: batchFilter,
        limit: 25,
        ...(reset || !nextCursor ? {} : { cursor: nextCursor }),
      });
      batches = reset ? res.batches : [...batches, ...res.batches];
      nextCursor = res.nextCursor;
      $('#bk-batches-more').classList.toggle('hidden', !nextCursor);
      await renderBatches();
    } catch (err) {
      $('#bk-batches').innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  async function renderBatches(): Promise<void> {
    const el = $('#bk-batches');
    if (batches.length === 0) {
      el.innerHTML = '<p class="muted">None.</p>';
      return;
    }
    el.innerHTML = `<div class="data-table-wrap"><table class="data-table">
      <thead><tr><th>Created</th><th>Surface</th><th>Games</th><th>Rows</th><th>Status</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${batches.map(renderBatchRow).join('')}</tbody>
    </table></div>`;
    bindBatchButtons(el);
    for (const id of openBatches) {
      if (batches.some((b) => b.id === id)) await openDetail(id);
    }
  }

  function renderBatchRow(b: BulkBatchView): string {
    const mine = b.proposerUserId === deps.userId;
    const actions: string[] = [`<button class="ghost" data-view="${b.id}">${openBatches.has(b.id) ? 'Hide' : 'View'}</button>`];
    if (b.status === 'pending') {
      if (deps.isSuperAdmin && !mine) actions.push(`<button class="primary" data-approve="${b.id}">Approve</button>`);
      if (deps.can('game.config.approve')) actions.push(`<button class="ghost" data-reject="${b.id}">${mine ? 'Withdraw' : 'Reject'}</button>`);
    }
    if (b.status === 'approved' && deps.isSuperAdmin) {
      actions.push(`<button class="danger" data-revert="${b.id}">Revert</button>`);
    }
    const statusCls = b.status === 'pending' ? 'warn' : b.status === 'approved' ? 'live' : '';
    const decided =
      b.status === 'reverted'
        ? `<div class="muted">reverted ${fmtDateTime(b.revertedAt ?? '')}${b.revertReason ? ` — ${escapeHtml(b.revertReason)}` : ''}</div>`
        : b.status === 'rejected' && b.reason
          ? `<div class="muted">${escapeHtml(b.reason)}</div>`
          : '';
    return `<tr>
      <td>${fmtDateTime(b.createdAt)}${b.note ? `<div class="muted">${escapeHtml(b.note)}</div>` : ''}</td>
      <td><code>${escapeHtml(b.surface)}</code>${b.mode === 'inherit' ? ' <span class="pill">reset to provider default</span>' : ''}</td>
      <td>${b.gameIds.map((g) => escapeHtml(prettyGame(g))).join(', ')}</td>
      <td>${b.itemCount ?? '—'}</td>
      <td><span class="pill ${statusCls}">${escapeHtml(b.status)}</span>${decided}</td>
      <td><code>${escapeHtml(b.proposerUserId.slice(0, 8))}…</code>${mine ? ' <span class="muted">(you)</span>' : ''}</td>
      <td style="white-space:nowrap">${actions.join(' ')}</td>
    </tr>
    <tr class="${openBatches.has(b.id) ? '' : 'hidden'}" data-detail="${b.id}"><td colspan="7"><p class="muted">Loading…</p></td></tr>`;
  }

  async function openDetail(batchId: string): Promise<void> {
    const cell = root.querySelector<HTMLElement>(`tr[data-detail="${batchId}"] td`);
    if (!cell) return;
    try {
      const { batch, items, outOfEnvelope, supersedes } = await deps.client.getBulkBatch(batchId);
      cell.innerHTML = `
        <div class="bulk-detail">
          ${supersedeSummary(supersedes)}
          ${envelopeSummary(outOfEnvelope)}
          ${
            batch.mode === 'inherit'
              ? '<div class="muted">Removes each integrator’s own value so it follows the provider default.</div>'
              : `<div class="muted" style="margin-bottom:6px">Proposed value</div>${jsonBlock(batch.pendingConfig)}`
          }
          <div class="data-table-wrap" style="margin-top:10px"><table class="data-table">
            <thead><tr><th>Integrator</th><th>Game</th><th>Row status</th><th>Before</th><th>After</th></tr></thead>
            <tbody>${items
              .map(
                (i) => `<tr>
                  <td>${escapeHtml(i.integratorName ?? i.integratorId)} <code>${escapeHtml(i.integratorSlug ?? '')}</code></td>
                  <td>${escapeHtml(prettyGame(i.gameId))}</td>
                  <td><span class="pill">${escapeHtml(i.status)}</span>${i.reason ? `<div class="muted">${escapeHtml(i.reason)}</div>` : ''}${supersedeWarning(i.conflictingApprovalIds)}${envelopeWarning(i.envelopeViolations)}</td>
                  <td>${jsonBlock(i.prevConfig, 'no override')}</td>
                  <td>${jsonBlock(i.pendingConfig, 'provider default')}</td>
                </tr>`,
              )
              .join('')}</tbody>
          </table></div>
        </div>`;
    } catch (err) {
      cell.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function bindBatchButtons(scope: HTMLElement): void {
    scope.querySelectorAll<HTMLButtonElement>('button[data-view]').forEach((btn) =>
      btn.addEventListener('click', async () => {
        const id = btn.dataset.view!;
        const row = scope.querySelector<HTMLElement>(`tr[data-detail="${id}"]`)!;
        if (openBatches.has(id)) {
          openBatches.delete(id);
          row.classList.add('hidden');
          btn.textContent = 'View';
          return;
        }
        openBatches.add(id);
        row.classList.remove('hidden');
        btn.textContent = 'Hide';
        await openDetail(id);
      }),
    );

    scope.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) =>
      btn.addEventListener('click', async () => {
        const b = batches.find((x) => x.id === btn.dataset.approve)!;
        const ok = await askConfirm(
          b.mode === 'inherit'
            ? `Remove ${b.surface} overrides from ${b.itemCount ?? 'every'} integrator × game row(s) now? They will follow the provider default immediately.`
            : `Apply ${b.surface} to ${b.itemCount ?? 'every'} integrator × game row(s) now? This writes live config.`,
        );
        if (!ok) return;
        btn.disabled = true;
        try {
          const res = await deps.client.approveBulkBatch(b.id);
          const o = res.outcomes;
          const closed = res.supersededApprovalIds?.length ?? 0;
          showToast(
            `Applied to ${o?.applied ?? 0} row(s)${o?.missing ? `; ${o.missing} no longer had the game` : ''}${
              closed ? `; closed ${closed} older pending request(s)` : ''
            }.`,
            'success',
          );
          if (res.auditFailures) showToast(`${res.auditFailures} audit write(s) failed — check server logs.`, 'warn');
          await loadBatches(true);
        } catch (err) {
          btn.disabled = false;
          showToast(describe(err), 'error');
        }
      }),
    );

    scope.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) =>
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Reason?');
        if (!reason) return;
        try {
          await deps.client.rejectBulkBatch(btn.dataset.reject!, reason);
          showToast('Batch rejected.', 'success');
          await loadBatches(true);
        } catch (err) {
          showToast(describe(err), 'error');
        }
      }),
    );

    scope.querySelectorAll<HTMLButtonElement>('button[data-revert]').forEach((btn) =>
      btn.addEventListener('click', async () => {
        const id = btn.dataset.revert!;
        const reason = await askPrompt('Revert reason? Each integrator gets its value from before this batch back.');
        if (!reason) return;
        btn.disabled = true;
        try {
          await revert(id, reason, false);
        } catch (err) {
          if (err instanceof AdminApiError && err.code === 'NOTHING_REVERTED') {
            const force = await askConfirm(
              'Every integrator has changed this surface since the batch applied, or no longer has the game. Overwrite their newer values anyway?',
              { danger: true },
            );
            if (force) {
              try {
                await revert(id, reason, true);
                return;
              } catch (forceErr) {
                showToast(describe(forceErr), 'error');
              }
            }
          } else {
            showToast(describe(err), 'error');
          }
          btn.disabled = false;
        }
      }),
    );
  }

  async function revert(batchId: string, reason: string, force: boolean): Promise<void> {
    const res = await deps.client.revertBulkBatch(batchId, { reason, force });
    const o = res.outcomes;
    showToast(
      `Reverted ${o?.reverted ?? 0} row(s)${o?.drifted ? `; skipped ${o.drifted} changed since` : ''}.`,
      o?.drifted ? 'warn' : 'success',
    );
    await loadBatches(true);
  }

  paintSurfaceHint();
  await loadBatches(true);
}

function jsonBlock(value: unknown, emptyLabel = '—'): string {
  if (value === null || value === undefined) return `<span class="muted">${escapeHtml(emptyLabel)}</span>`;
  const text = JSON.stringify(value, null, 2);
  const oneLine = JSON.stringify(value);
  if (oneLine.length <= 60) return `<code>${escapeHtml(oneLine)}</code>`;
  return `<details><summary class="muted">${escapeHtml(oneLine.slice(0, 48))}…</summary><div class="diff">${escapeHtml(text)}</div></details>`;
}

/** Banner for the pending single requests approving the batch will close. */
function supersedeSummary(count: number): string {
  if (!count) return '';
  return `<p class="warn-text">Approving this change will reject ${count} older pending request(s) on the same integrator, game and setting, marked “superseded by bulk change”. Otherwise one of them could be approved later and undo this change for that integrator.</p>`;
}

function supersedeWarning(ids: readonly string[] | undefined): string {
  if (!ids?.length) return '';
  return `<div class="warn-text">will close ${ids.length} older pending request(s)</div>`;
}

/** Banner for rows whose stored values would sit outside the new envelope. */
function envelopeSummary(count: number): string {
  if (!count) return '';
  return `<p class="warn-text">${count} integrator × game row(s) already run values outside the envelope this change sets. The envelope is only checked when a tenant saves, so those values keep running until the tenant next saves or someone corrects them.</p>`;
}

/** Per-row list of stored values outside the new envelope, e.g. `table.maxBet 400000 > 100000`. */
function envelopeWarning(violations: readonly BulkEnvelopeViolation[] | undefined): string {
  if (!violations?.length) return '';
  const list = violations
    .map((v) => `${escapeHtml(v.surface)}.${escapeHtml(v.field)} ${v.value} ${v.bound === 'max' ? '&gt;' : '&lt;'} ${v.limit}`)
    .join(', ');
  return `<div class="warn-text">already outside new envelope: ${list}</div>`;
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

/** Zod `flatten()` details from INVALID_BODY, per game, as one readable line. */
function describeWithDetails(err: unknown): string {
  if (!(err instanceof AdminApiError) || !err.details || typeof err.details !== 'object') return describe(err);
  type Flat = { formErrors?: string[]; fieldErrors?: Record<string, string[]> };
  const line = (flat: Flat) =>
    [
      ...(flat.formErrors ?? []),
      ...Object.entries(flat.fieldErrors ?? {}).map(([f, e]) => `${f}: ${e.join(', ')}`),
    ].join('; ') || 'invalid';
  const details = err.details as Record<string, unknown>;
  // A request-shape error is one flatten(); a value error is one per game.
  if ('fieldErrors' in details || 'formErrors' in details) return `${err.code}: ${line(details as Flat)}`;
  const parts = Object.entries(details).map(([game, flat]) => `${prettyGame(game)} — ${line(flat as Flat)}`);
  return `${err.code}: ${parts.join(' | ')}`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
